/**
 * The form a vendor fills in themselves.
 *
 * The only door into vendor onboarding. There used to be three more pages of
 * buttons behind `/registry-test` where an operator drove the same thing by
 * hand, one Safe transaction at a time; `onboardVendor` made that sequence a
 * single call and the server files it on a ticker, so the console had nothing
 * left to do and was deleted. Everything between this form and a minted
 * certificate now happens without anybody opening a page.
 *
 * Submitting is not an application being accepted, and the page says so twice —
 * once before the button and once after. That is the honest description: the
 * form writes a row to a file the portal owns, and nothing happens on-chain
 * until a quorum of Safe owners signs. Promising anything warmer would set up a
 * wait with no end date attached.
 *
 * Nothing here connects a wallet. The address is typed, because the wallet that
 * will *hold* the credential is frequently not the one whoever fills in this
 * form is carrying — and asking for a signature to prove otherwise would be
 * asking a stranger to sign something on a page they have no reason to trust
 * yet. See the phishing line in the certificate mail for the other half of that
 * position.
 */
import { useState, type FormEvent } from "react";
import { isAddress } from "viem";

import { Alert, Button, Card, PageHeader } from "@/components/ui";
import { Field } from "@/components/fields";
import { NETWORK_NAME } from "@/lib/config";
import { usePageMeta } from "@/lib/page-meta";
import {
  emailProblem,
  expiryToUnix,
  submitApplication,
  type ApplicationDraft,
} from "@/lib/registry/applications";

const CONTEXT = "Pendaftaran vendor";

type FormState = {
  vendorName: string;
  category: string;
  website: string;
  wallet: string;
  email: string;
  expiresAt: string;
};

const EMPTY: FormState = {
  vendorName: "",
  category: "",
  website: "",
  wallet: "",
  email: "",
  expiresAt: "",
};

/**
 * Restates what the server will check, so a mistake is caught while the person
 * who made it is still looking at the field.
 *
 * A filter, never a substitute: `vendorapply.Validate` runs on every submission
 * regardless, and it is the copy that decides. Both exist for the same reason
 * the register form has its own copy — the alternative is a round trip that
 * comes back saying only that something was wrong.
 */
function problems(form: FormState): Partial<Record<keyof FormState, string>> {
  const found: Partial<Record<keyof FormState, string>> = {};

  if (!form.vendorName.trim()) {
    found.vendorName = "Wajib diisi — nama ini yang tercetak di sertifikat.";
  } else if ([...form.vendorName.trim()].length > 120) {
    found.vendorName = "Maksimal 120 karakter.";
  }

  if (!form.wallet.trim()) {
    found.wallet = "Wajib diisi — sertifikatnya dikirim ke alamat ini.";
  } else if (!isAddress(form.wallet.trim())) {
    found.wallet = "Bukan alamat wallet yang valid.";
  }

  const email = emailProblem(form.email);
  if (email) found.email = email;

  if (form.website.trim() && !/^https?:\/\/\S+$/.test(form.website.trim())) {
    found.website = "Harus URL lengkap, contoh: https://contoh.co.id.";
  }

  if (form.expiresAt) {
    const seconds = expiryToUnix(form.expiresAt);
    if (seconds !== 0n && seconds <= BigInt(Math.floor(Date.now() / 1000))) {
      found.expiresAt = "Tanggalnya harus di masa depan.";
    }
  }

  return found;
}

export function VendorApplyPage() {
  usePageMeta({
    title: "Pendaftaran vendor",
    description:
      "Ajukan sertifikat vendor resmi ASETQU. Pengajuan ditinjau dan disetujui bersama " +
      "oleh pemegang kunci multisig sebelum sertifikat diterbitkan.",
  });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);
  // Errors appear after the first attempt, not while somebody is still typing
  // their company name into an empty field.
  const [attempted, setAttempted] = useState(false);

  const errors = problems(form);
  const valid = Object.keys(errors).length === 0;

  function set<K extends keyof FormState>(key: K, value: string) {
    setForm((previous) => ({ ...previous, [key]: value }));
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setAttempted(true);
    if (!valid || busy) return;

    setBusy(true);
    setFailure(null);
    try {
      const draft: ApplicationDraft = {
        vendorName: form.vendorName.trim(),
        category: form.category.trim(),
        website: form.website.trim(),
        wallet: form.wallet.trim(),
        email: form.email.trim(),
        expiresAt: Number(expiryToUnix(form.expiresAt)),
      };
      setReceipt(await submitApplication(draft));
      setForm(EMPTY);
      setAttempted(false);
    } catch (error) {
      setFailure(error instanceof Error ? error.message : "Pengajuan gagal dikirim.");
    } finally {
      setBusy(false);
    }
  }

  if (receipt) {
    return (
      <div className="page-shell">
        <PageHeader
          context={CONTEXT}
          title="Cek email Anda"
          description="Pengajuannya belum berjalan sampai Anda membuka tautan konfirmasi yang baru kami kirim."
        />
        <Card title="Satu klik lagi">
          <div className="space-y-3 text-sm text-ink-muted">
            <p>
              Kami mengirim tautan konfirmasi ke alamat yang Anda isi. Sampai tautan itu
              dibuka, pengajuan Anda hanya tersimpan di sini dan tidak diajukan ke mana pun.
            </p>
            <p>
              Tidak ada di kotak masuk? Cek folder spam. Tautannya hanya bisa dipakai sekali,
              jadi kalau sudah pernah dibuka tidak perlu dibuka lagi.
            </p>
          </div>

          <p className="mt-5 mb-2 text-sm text-ink-muted">
            Nomor pengajuan Anda, kalau ingin menanyakannya lewat kanal resmi:
          </p>
          <code className="registry-console__hash">{receipt}</code>

          <div className="mt-5 space-y-3 text-sm text-ink-muted">
            <p>
              Begitu tautannya dibuka, pengajuan Anda otomatis masuk ke antrean dompet
              multisig ASETQU sebagai satu transaksi. Tidak ada langkah manual lain, dan
              tidak ada yang perlu Anda kejar.
            </p>
            <p>
              Yang tersisa adalah keputusan manusia: beberapa pemegang kunci membaca
              pengajuan itu dan menandatanganinya. Waktunya tergantung mereka, bukan pada
              sistem — jadi ini bukan hitungan menit, tapi juga tidak ada antrean
              administrasi di belakangnya.
            </p>
            <p>
              Saat tanda tangannya lengkap dan transaksinya dieksekusi, sertifikat NFT-nya
              langsung tercetak ke wallet yang Anda cantumkan di {NETWORK_NAME}, dan
              salinannya dikirim ke email yang sama. Ketiganya terjadi dalam satu transaksi
              — tidak ada keadaan di mana Anda terdaftar tapi sertifikatnya belum ada.
            </p>
          </div>

          <Alert tone="warn" title="Kami tidak pernah meminta seed phrase">
            ASETQU tidak pernah meminta seed phrase, private key, atau tanda tangan wallet —
            tidak lewat email, tidak lewat chat, tidak lewat halaman mana pun. Abaikan siapa
            pun yang memintanya dengan mengatasnamakan pengajuan ini.
          </Alert>
        </Card>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <PageHeader
        context={CONTEXT}
        title="Ajukan sertifikat vendor"
        description={
          <>
            Sertifikat vendor ASETQU adalah NFT non-transferable yang menyatakan sebuah
            perusahaan terdaftar resmi di ekosistem. Mengisi formulir ini adalah pengajuan —
            bukan pendaftaran, dan bukan jaminan diterima. Penerbitannya harus disetujui
            bersama oleh beberapa pemegang kunci.
          </>
        }
      />

      <div className="registry-console">
        <div className="registry-console__form">
          <Card
            title="Data perusahaan"
            description="Nama dan kategori inilah yang tercetak di sertifikat dan bisa diperiksa siapa saja."
          >
            <form className="space-y-5" onSubmit={onSubmit}>
              <Field
                id="a-name"
                label="Nama perusahaan"
                hint="Tulis persis seperti nama legalnya."
                error={attempted ? errors.vendorName : undefined}
              >
                <input
                  id="a-name"
                  value={form.vendorName}
                  onChange={(event) => set("vendorName", event.target.value)}
                  placeholder="PT Properti Nusantara"
                  autoComplete="organization"
                />
              </Field>

              <Field id="a-category" label="Kategori (opsional)">
                <input
                  id="a-category"
                  value={form.category}
                  onChange={(event) => set("category", event.target.value)}
                  placeholder="Properti"
                />
              </Field>

              <Field
                id="a-website"
                label="Situs resmi (opsional)"
                error={attempted ? errors.website : undefined}
              >
                <input
                  id="a-website"
                  value={form.website}
                  onChange={(event) => set("website", event.target.value)}
                  placeholder="https://contoh.co.id"
                  autoComplete="url"
                  inputMode="url"
                />
              </Field>

              <Field
                id="a-wallet"
                label="Alamat wallet"
                hint={`Sertifikatnya dicetak ke alamat ini di ${NETWORK_NAME} dan tidak bisa dipindahkan setelahnya. Pastikan wallet-nya benar-benar Anda kuasai.`}
                error={attempted ? errors.wallet : undefined}
              >
                <input
                  id="a-wallet"
                  value={form.wallet}
                  onChange={(event) => set("wallet", event.target.value)}
                  placeholder="0x…"
                  spellCheck={false}
                  className="font-mono"
                />
              </Field>

              <Field
                id="a-email"
                label="Email"
                hint="Ke sini sertifikatnya dikirim. Tidak pernah ikut tercetak di sertifikat maupun di metadata publiknya."
                error={attempted ? errors.email : undefined}
              >
                <input
                  id="a-email"
                  type="email"
                  value={form.email}
                  onChange={(event) => set("email", event.target.value)}
                  placeholder="vendor@contoh.co.id"
                  autoComplete="email"
                />
              </Field>

              <Field
                id="a-expires"
                label="Berlaku sampai (opsional)"
                hint="Kosongkan kalau tidak ada tanggal berakhir."
                error={attempted ? errors.expiresAt : undefined}
              >
                <input
                  id="a-expires"
                  type="date"
                  value={form.expiresAt}
                  onChange={(event) => set("expiresAt", event.target.value)}
                />
              </Field>

              {failure ? <Alert tone="danger">{failure}</Alert> : null}

              <Button type="submit" disabled={busy}>
                {busy ? "Mengirim…" : "Kirim pengajuan"}
              </Button>
            </form>
          </Card>
        </div>

        <div className="registry-console__aside">
          <Card title="Yang terjadi setelah ini">
            <ol className="space-y-3 text-sm text-ink-muted">
              <li>
                Anda membuka tautan konfirmasi di email. Sebelum ini tidak ada apa pun yang
                diajukan atas nama Anda.
              </li>
              <li>
                Pengajuannya masuk sendiri ke antrean dompet multisig ASETQU, sebagai{" "}
                <strong>satu</strong> transaksi. Tidak ada operator yang perlu menyalinnya
                atau menekan tombol.
              </li>
              <li>
                Beberapa pemegang kunci membacanya dan menandatanganinya. Ini satu-satunya
                langkah yang menunggu manusia, dan satu-satunya cara pengajuan bisa ditolak.
              </li>
              <li>
                Eksekusinya melakukan semuanya sekaligus: perusahaan Anda tercatat resmi,
                sertifikat NFT-nya dicetak ke wallet Anda, dan token itu langsung tertaut ke
                record-nya. Kalau satu bagian gagal, semuanya batal — tidak ada setengah
                jadi.
              </li>
              <li>Salinan sertifikatnya dikirim ke email Anda segera setelah itu.</li>
            </ol>
          </Card>

          <Card title="Yang tidak akan pernah kami minta">
            <Alert tone="warn">
              ASETQU tidak pernah meminta seed phrase, private key, atau tanda tangan wallet
              lewat email atau chat. Halaman ini pun tidak meminta Anda menghubungkan wallet.
            </Alert>
          </Card>
        </div>
      </div>
    </div>
  );
}
